/**
 * Chuẩn hóa response Wise (get-required-fields-gsn khi gateway = Wise) sang schema dùng chung.
 * Wise format: { type, quoteId, title, fields: [ { name, group: [ { key, name, type, refreshRequirementsOnChange, required, example, valuesAllowed, validationRegexp } ] } ] }
 * Ref: https://docs.wise.com/api-reference/transfer#transfer-requirements
 */

/**
 * Transfer type ở root mỗi object trong mảng response (vd `type: "email"`) — không support tab đó.
 * Chỉ so khớp field root `type`, không áp vào `fields[].group[].type` (text/select/…) hay key path trong ACH/Wire/SWIFT.
 */
const EXCLUDED_WISE_VARIANT_ROOT_TYPES = new Set(['email']);

function isExcludedWiseVariantRootType(rootTransferType) {
  return EXCLUDED_WISE_VARIANT_ROOT_TYPES.has(String(rootTransferType || '').toLowerCase());
}

/**
 * Kiểm tra response có phải format Wise (có fields[].group) hay không.
 * @param {Object} response
 * @returns {boolean}
 */
export function isWiseFormatResponse(response) {
  const fields = response?.fields;
  if (!Array.isArray(fields) || fields.length === 0) return false;
  const first = fields[0];
  return Array.isArray(first?.group) && first.group.length > 0;
}

/**
 * Response get-required-fields-gsn (Wise) có thể là mảng nhiều phương thức (ACH / Wire / SWIFT / …).
 * @param {*} response
 * @returns {boolean}
 */
export function isWiseMultiVariantResponse(response) {
  if (!Array.isArray(response) || response.length === 0) return false;
  return response.every((item) => item && isWiseFormatResponse(item));
}

/**
 * @param {Array<Object>} wiseResponseList — mỗi phần tử giống single Wise object (type, quoteId, title, fields)
 * @returns {{ condition: object, wiseVariants: Array<{ variantKey: string, type?: string, quoteId?: string, title?: string, fields: import('./fieldSchemaTypes').FieldSchema[] }> }}
 */
export function normalizeWiseMultiVariantResponse(wiseResponseList) {
  const filtered = (wiseResponseList || []).filter(
    (item) => item && !isExcludedWiseVariantRootType(item?.type)
  );
  const wiseVariants = filtered.map((item, index) => {
    const normalized = normalizeWiseResponseToSchema(item);
    const variantKey = item?.type || normalized.type || `variant-${index}`;
    return {
      ...normalized,
      variantKey,
    };
  });
  return { condition: {}, wiseVariants };
}

/**
 * @param {Object} wiseResponse
 * @returns {{ condition: object, fields: import('./fieldSchemaTypes').FieldSchema[], type?: string, quoteId?: string, title?: string }}
 */
export function normalizeWiseResponseToSchema(wiseResponse) {
  const result = { condition: {}, fields: [] };
  if (!wiseResponse || !Array.isArray(wiseResponse.fields)) return result;
  result.type = wiseResponse.type;
  result.quoteId = wiseResponse.quoteId;
  result.title = wiseResponse.title;
  /* Response là một object đơn với root type email — không render form (không liên quan field con trong variant khác). */
  if (isExcludedWiseVariantRootType(wiseResponse.type)) {
    result.fields = [];
    return result;
  }
  const fieldList = [
    {
      enabled: true,
      path: 'accountHolderName',
      required: true,
      field: {
        key: 'accountHolderName',
        label: 'Account holder name',
        type: 'INPUT',
        placeholder: 'e.g. John Doe',
        refresh: false,
        options: [],
      },
      rule: undefined,
    },
  ];
  wiseResponse.fields.forEach((section) => {
    const group = section?.group;
    if (!Array.isArray(group)) return;
    group.forEach((g) => {
      const key = g?.key;
      if (!key) return;
      const type = mapWiseTypeToFieldType(g?.type);
      const options = Array.isArray(g?.valuesAllowed)
        ? g.valuesAllowed.map((v) => ({ label: v?.name ?? v?.key ?? '', value: v?.key ?? '' }))
        : [];
      fieldList.push({
        enabled: true,
        path: key,
        required: Boolean(g?.required),
        field: {
          key,
          label: g?.name ?? key,
          type,
          placeholder: g?.example ?? '',
          refresh: Boolean(g?.refreshRequirementsOnChange),
          options,
        },
        rule: g?.validationRegexp ? { pattern: g.validationRegexp } : undefined,
      });
    });
  });
  result.fields = fieldList;
  return result;
}

function mapWiseTypeToFieldType(wiseType) {
  const t = String(wiseType || '').toLowerCase();
  if (t === 'select') return 'SELECT';
  if (t === 'radio') return 'RADIO';
  return 'INPUT';
}
